import { useCallback, useEffect, useMemo, useState } from 'react';
import { SimState, UsageModel } from '@bymason/controller-api-hooks';
import { Box, DataGridContainer, Fade, GridDensity } from '@bymason-private/legacy-pattern-components';
import ByDayGrid from '../by-day-grid';
import BySimGrid from '../by-sim-grid';
import SimStateSelector from '../sim-state-selector';
import { Route } from '@bymason-private/route-provider';

const BY_SIM_GRID_DENSITY_KEY = 'by-sim-grid-density';
const BY_DAY_GRID_DENSITY_KEY = 'by-day-grid-density';

interface ConnectivityGridContainerProps {
  iccid?: string;
  reportMonth: Date | null;
  usageByDay: void | UsageModel[];
  simConnectivityRoute: Route;
  isScrolledToBottom?: boolean;
  onError: (error: string | null) => void;
}

export default function ConnectivityGridContainer({
  iccid,
  onError,
  reportMonth,
  usageByDay,
  simConnectivityRoute,
  isScrolledToBottom,
}: ConnectivityGridContainerProps) {
  const [simStatusFilter, setSimStatusFilter] = useState<SimState | undefined>(undefined);
  const [activeGridIndex, setActiveGridIndex] = useState(0);
  const [byDayGridDensity, setByDayGridDensity] = useState<GridDensity>('standard');
  const [bySimGridDensity, setBySimGridDensity] = useState<GridDensity>('standard');
  const [bySimHasNext, setBySimHasNext] = useState(false);
  const [bySimPageNumber, setBySimPageNumber] = useState(1);
  const [bySimBusy, setBySimBusy] = useState(false);

  const handleGridIndexChange = useCallback((newIndex: number) => {
    setActiveGridIndex(newIndex);
  }, []);

  const handleBySimBusyChange = useCallback((busy: boolean) => {
    setBySimBusy(busy);
  }, []);

  const handleBySimErrorChange = useCallback(
    (error: string | null) => {
      onError(error);
    },
    [onError]
  );

  const handleBySimStateFilterChange = useCallback((status?: SimState) => {
    setBySimPageNumber(1);
    setSimStatusFilter(status);
  }, []);

  const handleBySimStateSortModelChange = useCallback(() => {
    setBySimPageNumber(1);
  }, []);

  const handleBySimDensityChange = useCallback((newDensity: GridDensity) => {
    localStorage.setItem(BY_SIM_GRID_DENSITY_KEY, newDensity);
    setBySimGridDensity(newDensity);
  }, []);

  const handleBySimDataInfo = useCallback((hasNext: boolean) => {
    setBySimHasNext(hasNext);
  }, []);

  const handleBySimPageChange = useCallback(
    (direction: 'next' | 'previous') => {
      setBySimPageNumber(direction === 'next' ? bySimPageNumber + 1 : bySimPageNumber - 1);
    },
    [bySimPageNumber]
  );

  const handleByDayDensityChange = useCallback((newDensity: GridDensity) => {
    localStorage.setItem(BY_DAY_GRID_DENSITY_KEY, newDensity);
    setByDayGridDensity(newDensity);
  }, []);

  const gridToggles = useMemo(
    () => [
      {
        disablePageNext: !bySimHasNext || bySimBusy,
        disablePagePrev: bySimPageNumber === 1 || bySimBusy,
        headerContent: <SimStateSelector onChange={handleBySimStateFilterChange} value={simStatusFilter} />,
        onDensityChange: handleBySimDensityChange,
        onPageChange: handleBySimPageChange,
        page: bySimPageNumber,
        // pagination: true,
        toggleLabel: 'By SIM',
      },
      {
        onDensityChange: handleByDayDensityChange,
        toggleLabel: 'By Day',
      },
    ],
    [
      bySimBusy,
      bySimHasNext,
      bySimPageNumber,
      handleByDayDensityChange,
      handleBySimDensityChange,
      handleBySimPageChange,
      handleBySimStateFilterChange,
      simStatusFilter,
    ]
  );

  //
  // handle grid density initialization
  //
  useEffect(() => {
    const cachedBySimDensity = localStorage.getItem(BY_SIM_GRID_DENSITY_KEY);
    const cachedByDayDensity = localStorage.getItem(BY_DAY_GRID_DENSITY_KEY);

    if (cachedBySimDensity) {
      setBySimGridDensity(cachedBySimDensity as GridDensity);
    }

    if (cachedByDayDensity) {
      setByDayGridDensity(cachedByDayDensity as GridDensity);
    }
  }, []);

  return (
    <Box
      sx={{
        '& .MasonDataGridContainer-root': (theme) => ({
          [theme.breakpoints.down(1100)]: {
            minWidth: 820,
          },
        }),
      }}
    >
      {iccid ? (
        <Fade in={activeGridIndex === 0} timeout={{ enter: 750, exit: 0 }}>
          <Box
            sx={{
              borderRadius: 2,
            }}
          >
            <ByDayGrid density={byDayGridDensity} show={true} usageByDay={usageByDay} />
          </Box>
        </Fade>
      ) : (
        <DataGridContainer
          activeDensity={activeGridIndex === 0 ? bySimGridDensity : byDayGridDensity}
          activeGridIndex={activeGridIndex}
          gridToggles={gridToggles}
          minToggleWidth={100}
          onGridIndexChange={handleGridIndexChange}
        >
          <Fade in={activeGridIndex === 0} timeout={{ enter: 750, exit: 0 }}>
            <div>
              <BySimGrid
                density={bySimGridDensity}
                onBusyChange={handleBySimBusyChange}
                onDataInfo={handleBySimDataInfo}
                onErrorChange={handleBySimErrorChange}
                onSortModelChange={handleBySimStateSortModelChange}
                page={bySimPageNumber}
                isScrolledToBottom={isScrolledToBottom}
                // pagination
                reportMonth={reportMonth}
                show={activeGridIndex === 0}
                statusFilter={simStatusFilter}
                simConnectivityRoute={simConnectivityRoute}
              />
            </div>
          </Fade>
          <Fade in={activeGridIndex === 1} timeout={{ enter: 750, exit: 0 }}>
            <div>
              <ByDayGrid density={byDayGridDensity} show={activeGridIndex === 1} usageByDay={usageByDay} />
            </div>
          </Fade>
        </DataGridContainer>
      )}
    </Box>
  );
}
